import { makeAutoObservable, runInAction } from 'mobx';
import { signInWithEmail, signOutApp, db } from '@stores/environments/Database.web';
import { User } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { logService } from '../services/LogService';

class AuthStore {
  _isLogged = false;
  _loading = false;
  _user: User | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Verifica se o usuário tem acesso admin no Firebase Database
   * @param userUid - UID do usuário autenticado
   * @returns Promise<boolean> - true se tem acesso, false caso contrário
   */
  private async checkAdminAccess(userUid: string): Promise<boolean> {
    try {
      const adminRef = ref(db, `rules/auth/${userUid}`);
      const snapshot = await get(adminRef);
      return snapshot.exists() && snapshot.val() === true;
    } catch (error) {
      console.error('Erro ao verificar acesso admin:', error);
      return false;
    }
  }

  login = async (email: string, password: string) => {
    this._loading = true;
    try {
      const user = await signInWithEmail(email, password);
      
      if (!user || !user.uid) {
        throw new Error('Usuário não encontrado');
      }

      // Verifica se o usuário tem acesso admin
      const hasAdminAccess = await this.checkAdminAccess(user.uid);
      
      if (!hasAdminAccess) {
        // Faz logout se não tem acesso
        await signOutApp();
        throw new Error('Usuário não possui acesso ao painel administrativo');
      }

      runInAction(() => {
        this._isLogged = true;
        this._user = user;
      });

      // Registra o log de login
      const logData = logService.createLogData(
        'Auth',
        user.uid,
        `auth/${user.uid}`,
        { email: user.email, uid: user.uid },
        `Login realizado com sucesso: ${user.email}`
      );
      await logService.logOperation(logData);
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    } finally {
      runInAction(() => {
        this._loading = false;
      });
    }
  };

  logout = async () => {
    this._loading = true;
    try {
      // Registra o log de logout antes de fazer logout
      if (this._user) {
        const logData = logService.createLogData(
          'Auth',
          this._user.uid,
          `auth/${this._user.uid}`,
          { email: this._user.email, uid: this._user.uid },
          `Logout realizado: ${this._user.email}`
        );
        await logService.logOperation(logData);
      }

      await signOutApp();
      runInAction(() => {
        this._isLogged = false;
        this._user = null;
      });
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      runInAction(() => {
        this._loading = false;
      });
    }
  };
}

export const authStore = new AuthStore();
